import { AccuracyKeyMetricsData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/KeyMetricsAdapter';
import { ContentAccuracyTitles } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/ContentBarPercent/utils';

export interface FormattedAccuracyData {
  title: string;
  primary: number;
  details: string;
  lowProducts: number;
}

export const formatAccuracyData = (
  rawData: AccuracyKeyMetricsData['rawData'],
  isProductEntity: boolean,
  includeItemHighlights = false
): FormattedAccuracyData[] => {
  const { mainKeyMetrics } = rawData;
  const mainMeta = mainKeyMetrics.metadataMetrics;
  const toPercent = (value: number) => value * 100;

  // TITLE
  const titleCount = mainMeta?.titleMetrics?.actualCountNumerator || 0;
  let titleDetails = `${Math.round(titleCount).toLocaleString()} (of ${(mainMeta?.titleMetrics?.expectedCountDenominator || 0).toLocaleString()}) titles are accurate`;
  if (isProductEntity) {
    titleDetails = titleCount === 1 ? 'The title is accurate' : 'The title is inaccurate';
  }

  // ITEM HIGHLIGHTS
  const itemHighlightsScore = mainKeyMetrics.itemHighlightsScore ?? 0;
  const itemHighlightsMetadataCount = mainMeta?.itemHighlightsMetrics?.actualCountNumerator;
  const itemHighlightsMetadataTotal = mainMeta?.itemHighlightsMetrics?.expectedCountDenominator;
  const itemHighlightsProductTotal = mainKeyMetrics.totalProducts ?? 0;
  const itemHighlightsAccurateProducts =
    itemHighlightsProductTotal - (mainKeyMetrics.productsWithInaccurateItemHighlights ?? 0);
  const itemHighlightsCount = itemHighlightsMetadataCount ?? itemHighlightsAccurateProducts;
  const itemHighlightsTotal = itemHighlightsMetadataTotal ?? itemHighlightsProductTotal;
  let itemHighlightsDetails = `${Math.round(itemHighlightsCount).toLocaleString()} (of ${itemHighlightsTotal.toLocaleString()}) item highlights are accurate`;
  if (isProductEntity) {
    itemHighlightsDetails =
      itemHighlightsScore === 1
        ? 'The item highlights are accurate'
        : 'The item highlights are inaccurate';
  }

  // BULLET POINTS
  const bulletPointsCount = mainMeta?.bulletPointsMetrics?.actualCountNumerator || 0;
  let bulletPointsDetails = `${Math.round(bulletPointsCount).toLocaleString()} (of ${(mainMeta?.bulletPointsMetrics?.expectedCountDenominator || 0).toLocaleString()}) bullet points are accurate`;
  if (isProductEntity) {
    bulletPointsDetails = `${bulletPointsCount} ${bulletPointsCount <= 1 ? 'bullet point is' : 'bullet points are'} accurate`;
  }

  // DESCRIPTIONS
  const descriptionsCount = mainMeta?.descriptionMetrics?.actualCountNumerator || 0;
  let descriptionsDetails = `${Math.round(descriptionsCount).toLocaleString()} (of ${(mainMeta?.descriptionMetrics?.expectedCountDenominator || 0).toLocaleString()}) descriptions are accurate`;
  if (isProductEntity) {
    descriptionsDetails = descriptionsCount === 1 ? 'The description is accurate' : 'The description is inaccurate';
  }

  // IMAGES
  const imagesCount = mainMeta?.imageMetrics?.actualCountNumerator || 0;
  let imagesDetails = `${Math.round(imagesCount).toLocaleString()} (of ${(mainMeta?.imageMetrics?.expectedCountDenominator || 0).toLocaleString()}) images are accurate`;
  if (isProductEntity) {
    imagesDetails = `${imagesCount} ${imagesCount <= 1 ? 'image is' : 'images are'} accurate`;
  }

  // VIDEOS
  const videosCount = mainMeta?.videoMetrics?.actualCountNumerator || 0;
  let videosDetails = `${Math.round(videosCount).toLocaleString()} (of ${(mainMeta?.videoMetrics?.expectedCountDenominator || 0).toLocaleString()}) video placements are accurate`;
  if (isProductEntity) {
    videosDetails = `${videosCount} ${videosCount <= 1 ? 'video is' : 'videos are'} accurate`;
  }

  const rows: FormattedAccuracyData[] = [
    {
      title: ContentAccuracyTitles.Titles,
      primary: toPercent(mainKeyMetrics.titleScore || 0),
      details: titleDetails,
      lowProducts: mainKeyMetrics.productsWithInaccurateTitle || 0
    }
  ];

  if (includeItemHighlights) {
    rows.push({
      title: ContentAccuracyTitles.ItemHighlights,
      primary: toPercent(mainKeyMetrics.itemHighlightsScore || 0),
      details: itemHighlightsDetails,
      lowProducts: mainKeyMetrics.productsWithInaccurateItemHighlights || 0
    });
  }

  rows.push(
    {
      title: ContentAccuracyTitles.BulletPoints,
      primary: toPercent(mainKeyMetrics.featureBulletsScore || 0),
      details: bulletPointsDetails,
      lowProducts: mainKeyMetrics.productsWithInaccurateBulletPoints || 0
    },
    {
      title: ContentAccuracyTitles.Descriptions,
      primary: toPercent(mainKeyMetrics.descriptionScore || 0),
      details: descriptionsDetails,
      lowProducts: mainKeyMetrics.productsWithInaccurateDescription || 0
    },
    {
      title: ContentAccuracyTitles.Images,
      primary: toPercent(mainKeyMetrics.imagesScore || 0),
      details: imagesDetails,
      lowProducts: mainKeyMetrics.productsWithInaccurateImage || 0
    },
    {
      title: ContentAccuracyTitles.Videos,
      primary: toPercent(mainKeyMetrics.videosScore || 0),
      details: videosDetails,
      lowProducts: mainKeyMetrics.productsWithInaccurateVideo || 0
    },
    {
      title: ContentAccuracyTitles.TotalContentAccuracy,
      primary: toPercent(mainKeyMetrics.contentAccuracyScore || 0),
      details: `${Math.round(mainMeta?.contentMetrics?.actualCountNumerator || 0).toLocaleString()} (of ${(mainMeta?.contentMetrics?.expectedCountDenominator || 0).toLocaleString()}) content elements are accurate`,
      lowProducts: mainKeyMetrics.productsWithInaccurateContent || 0
    }
  );

  return rows;
};
